import api from './client';
import type {
  XmlTerceirosResponse,
  XmlTerceirosFilters,
  ExportOptions,
  ParcelasNfeResponse,
  ErpPagamentoNfeResponse,
} from '../types/xmlTerceiros';

class XmlTerceirosApi {
  // Buscar XML de terceiros com paginação e filtros
  async getXmlTerceiros(filters?: XmlTerceirosFilters, page = 1, pageSize = 50) {
    const params = new URLSearchParams();
    
    // Parâmetros de paginação
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    
    // Busca geral (campo principal de busca)
    if (filters?.search) {
      params.append('search', filters.search);
    }
    
    // Filtro rápido de dias (apenas se não houver datas personalizadas)
    if (filters?.dias && !filters?.dataInicio && !filters?.dataFim) {
      params.append('dias', filters.dias.toString());
    }
    
    // Filtro de datas personalizadas
    if (filters?.dataInicio) {
      params.append('dataInicio', filters.dataInicio);
    }
    if (filters?.dataFim) {
      params.append('dataFim', filters.dataFim);
    }
    
    // Filtros avançados específicos
    if (filters?.pendente !== undefined) {
      params.append('pendente', filters.pendente.toString());
    }
    
    if (filters?.emitenteNome) {
      params.append('emitenteNome', filters.emitenteNome);
    }
    
    if (filters?.cnpj) {
      params.append('cnpj', filters.cnpj);
    }
    
    if (filters?.numeroNfe) {
      params.append('numeroNfe', filters.numeroNfe);
    }

    const queryString = params.toString();
    const url = `/xmlterceiros${queryString ? `?${queryString}` : ''}`;
    
    return api.get<XmlTerceirosResponse>(url);
  }

  // Baixar DANFE em PDF
  async baixarDanfe(nfeId: number) {
    const response = await api.get(`/xmlterceiros/${nfeId}/danfe`, {
      responseType: 'blob',
    });
    
    // Criar URL para download do arquivo
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Criar link de download
    const link = document.createElement('a');
    link.href = url;
    link.download = `DANFE_${nfeId}.pdf`;
    document.body.appendChild(link);
    link.click();
    
    // Limpar recursos
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  }

  // Baixar XML
  async baixarXml(nfeId: number) {
    const response = await api.get(`/xmlterceiros/${nfeId}/xml`, {
      responseType: 'blob',
    });
    
    // Criar URL para download do arquivo
    const blob = new Blob([response.data], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    
    // Criar link de download
    const link = document.createElement('a');
    link.href = url;
    link.download = `NFe_${nfeId}.xml`;
    document.body.appendChild(link);
    link.click();
    
    // Limpar recursos
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  }

  // Buscar parcelas/fatura/pagamento extraídos do XML da NFe
  async getParcelas(nfeId: number) {
    return api.get<ParcelasNfeResponse>(`/xmlterceiros/${nfeId}/parcelas`);
  }

  // Consultar situação no ERP (contas a pagar / parcelas liquidadas)
  async getErpPagamento(nfeId: number) {
    return api.get<ErpPagamentoNfeResponse>(`/xmlterceiros/${nfeId}/erp-pagamento`);
  }

  // Exportar arquivo (genérico)
  async exportarArquivo(options: ExportOptions) {
    if (options.tipo === 'pdf') {
      return this.baixarDanfe(options.nfeId);
    } else {
      return this.baixarXml(options.nfeId);
    }
  }
}

export const xmlTerceirosApi = new XmlTerceirosApi();